import { useEffect, type FC } from 'react'

import { Table } from '@skytech/ui'
import { Td } from '@skytech/ui/Table/Td'
import { Th } from '@skytech/ui/Table/Th'
import { getQueryFilter, getSortedList } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import Highlight from '@/components/Highlight'
import { NoResults } from '@/components/NoResults'
import { ResultCount } from '@/components/ResultCount'
import { useSearch, useSortParams } from '@/hooks/searchParams'
import { useFormattedNumberWithCurrency } from '@/hooks/use-formatted-number-with-currency'
import { useRenderLimit } from '@/hooks/use-render-limit'

import { useReportsContext } from '../index.context'

import adminQueries from '../../queries'
import type { ReportFilters, SalaryDeductionUsageReport } from '../../resources'
import adminResources from '../../resources'

export const SalaryDeductionUsage: FC<{
  filters: ReportFilters
}> = ({ filters }) => {
  const { searchFilter } = useSearch()
  const { isReportListLoading, setCount, setIsReportListLoading } = useReportsContext()
  const { locale } = useAppContext()
  const [{ querySort, querySortBy }, { handleSort }] =
    useSortParams<keyof (typeof reportsFiltered)[number]>()

  const { data: reports = [] } = useQuery(
    adminQueries.adminSalaryDeductionUsageReports(filters),
    async () => {
      const reports = await adminResources.getSalaryDeductionUsageReports(filters)
      setIsReportListLoading(false)
      return reports
    },
    { enabled: Boolean(filters.fromDate && filters.toDate) }
  )

  useEffect(() => {
    setCount(reports.length)
  }, [reports, setCount])

  const stringMatcher = (item: SalaryDeductionUsageReport) => [
    item.name,
    item.employeeNo,
    item.departmentCode,
    item.departmentName,
    item.vat,
    item.amount,
  ]

  const reportsFiltered = reports.filter(reportsObject =>
    getQueryFilter(searchFilter, stringMatcher)(reportsObject)
  )
  const sortedList = getSortedList({
    list: reportsFiltered,
    querySort,
    querySortBy,
    locale,
  })

  const { visibleList, ListLoaderComponent } = useRenderLimit(sortedList, 30)

  const total = reportsFiltered.length
  const { formatNumber } = useFormattedNumberWithCurrency()
  return (
    <>
      <ResultCount count={total} />
      {total > 0 ? (
        <Table>
          <thead>
            <tr>
              <Th onClick={handleSort} id="name">
                <Translation id="Name" />
              </Th>
              <Th onClick={handleSort} id="employeeNo">
                <Translation id="Employee No" />
              </Th>

              <Th onClick={handleSort} id="departmentCode">
                <Translation id="Department Code" />
              </Th>
              <Th onClick={handleSort} id="departmentName">
                <Translation id="Department Name" />
              </Th>
              <Th onClick={handleSort} id="vat" className="text-right">
                <Translation id="Vat" />
              </Th>
              <Th onClick={handleSort} id="amount" className="text-right">
                <Translation id="Amount" />
              </Th>
            </tr>
          </thead>
          <tbody>
            {visibleList?.map(
              ({ name, employeeNo, departmentCode, departmentName, vat, amount }, index) => (
                <tr key={`${departmentCode}-${index}`}>
                  <Td>
                    <Highlight text={name} pattern={searchFilter} />
                  </Td>
                  <Td>{employeeNo}</Td>
                  <Td>
                    <Highlight text={departmentCode} pattern={searchFilter} />
                  </Td>
                  <Td>
                    <Highlight text={departmentName} pattern={searchFilter} />
                  </Td>
                  <Td className="text-right">
                    <Highlight
                      text={formatNumber({ amount: Number(vat), precision: 2 })}
                      pattern={searchFilter}
                    />
                  </Td>
                  <Td className="text-right">
                    <Highlight
                      text={formatNumber({ amount: Number(amount), precision: 2 })}
                      pattern={searchFilter}
                    />
                  </Td>
                </tr>
              )
            )}
          </tbody>
        </Table>
      ) : (
        !isReportListLoading && <NoResults />
      )}
      <ListLoaderComponent />
    </>
  )
}
